import React, { useEffect, useState } from 'react';
import { Lock, X } from 'lucide-react';
import type { CutierInvoiceProfile } from '../../types/cutierInvoiceProfile';
import {
  saveCutierInvoiceProfile,
  sanitizeCutierInvoiceProfile,
  verifyCutierAdminPin,
  verifyCutierPin,
} from '../../services/cutierFirebaseService';
import { isValidIban, normalizeIban } from '../../utils/pe-papers/ibanValidation';
import { assessCutierInvoiceProfile } from '../../utils/cutier/cutierInvoiceProfileUtils';

export interface CutierInvoiceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: CutierInvoiceProfile;
  onSaved: (profile: CutierInvoiceProfile) => void;
}

const inputClass =
  'w-full rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2.5 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none';

const labelClass =
  'mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted';

const CutierInvoiceSettingsModal: React.FC<CutierInvoiceSettingsModalProps> = ({
  isOpen,
  onClose,
  profile,
  onSaved,
}) => {
  const [unlocked, setUnlocked] = useState(false);
  const [accessPin, setAccessPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [localState, setLocalState] = useState<CutierInvoiceProfile>(profile);
  const [saveError, setSaveError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setUnlocked(false);
      setAccessPin('');
      setPinError('');
      setSaveError('');
      return;
    }
    setLocalState(profile);
  }, [isOpen, profile]);

  if (!isOpen) return null;

  const patch = <K extends keyof CutierInvoiceProfile>(key: K, value: CutierInvoiceProfile[K]) => {
    setLocalState((prev) => ({ ...prev, [key]: value }));
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    // Partner-PIN (Ramin) oder Admin-Masterpasswort — ohne die Werte in der UI zu zeigen
    const [partnerOk, adminOk] = await Promise.all([
      verifyCutierPin(accessPin),
      verifyCutierAdminPin(accessPin),
    ]);
    if (partnerOk || adminOk) {
      setUnlocked(true);
      setAccessPin('');
    } else {
      setPinError('PIN / Passwort ungültig.');
    }
  };

  const buildNormalized = (): CutierInvoiceProfile =>
    sanitizeCutierInvoiceProfile({
      ...localState,
      iban: normalizeIban(localState.iban),
    });

  const handleSave = async () => {
    const normalized = buildNormalized();

    setBusy(true);
    setSaveError('');
    const result = await saveCutierInvoiceProfile(normalized);
    onSaved(normalized);
    setBusy(false);

    const check = assessCutierInvoiceProfile(normalized);
    const ibanWarn =
      normalized.iban && !isValidIban(normalized.iban)
        ? ' IBAN-Prüfziffer ungültig — bitte korrigieren.'
        : '';
    if (!result.savedRemote) {
      setSaveError(
        `Auf diesem Gerät gespeichert.${ibanWarn} Cloud-Sync fehlgeschlagen${result.error ? ` (${result.error})` : ''}. Bitte Speichern noch einmal drücken.`
      );
      return;
    }
    if (!check.complete) {
      setSaveError(`Gespeichert.${ibanWarn} Für die Rechnung fehlen noch: ${check.missing.join(', ')}`);
      return;
    }
    onClose();
  };

  const handleDismiss = () => {
    if (unlocked) {
      const normalized = buildNormalized();
      void saveCutierInvoiceProfile(normalized).then((result) => {
        onSaved(normalized);
        if (!result.savedRemote) {
          console.warn('[Cutier] draft save on close failed', result.error);
        }
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100010] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl border border-[#E6C299]/30 bg-pe-card text-pe-text shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-4 top-4 z-10 rounded-lg border border-pe-border-strong p-2 text-pe-muted hover:text-pe-text"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-pe-border-strong px-6 py-5 pr-14">
          <h3 className="text-lg font-black uppercase tracking-wider text-pe-accent-text">
            Rechnungsdaten & E-Rechnung
          </h3>
          <p className="mt-2 text-xs text-pe-muted">
            Bank, Steuer und Empfänger (CUTIER). Speichern bleibt unten sichtbar.
          </p>
        </div>

        {!unlocked ? (
          <form onSubmit={handleUnlock} className="space-y-4 p-6">
            <div className="flex items-center gap-3 rounded-xl border border-amber-700/40 bg-amber-950/30 p-4 text-sm text-amber-200">
              <Lock className="h-5 w-5 shrink-0" />
              <p>PIN oder Admin-Passwort erforderlich, um Rechnungsdaten zu öffnen.</p>
            </div>
            <label className="block">
              <span className={labelClass}>PIN / Admin-Passwort</span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                className={inputClass}
                value={accessPin}
                onChange={(e) => setAccessPin(e.target.value)}
                placeholder="••••"
                autoFocus
              />
            </label>
            {pinError ? <p className="text-xs text-red-400">{pinError}</p> : null}
            <button
              type="submit"
              disabled={!accessPin.trim()}
              className="w-full rounded-xl bg-[#E6C299] py-3 text-xs font-black uppercase tracking-widest text-black disabled:opacity-50"
            >
              Entsperren
            </button>
          </form>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text">
              Aussteller (Ramin Dell)
            </p>
            <label className="block">
              <span className={labelClass}>Firmenname</span>
              <input className={inputClass} value={localState.displayName} onChange={(e) => patch('displayName', e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className={labelClass}>Straße</span>
                <input className={inputClass} value={localState.street} onChange={(e) => patch('street', e.target.value)} />
              </label>
              <label className="block">
                <span className={labelClass}>PLZ</span>
                <input className={inputClass} value={localState.zip} onChange={(e) => patch('zip', e.target.value)} />
              </label>
              <label className="block">
                <span className={labelClass}>Ort</span>
                <input className={inputClass} value={localState.city} onChange={(e) => patch('city', e.target.value)} />
              </label>
            </div>
            <label className="block">
              <span className={labelClass}>E-Mail</span>
              <input type="email" className={inputClass} value={localState.email} onChange={(e) => patch('email', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>Telefon (optional)</span>
              <input className={inputClass} value={localState.phone} onChange={(e) => patch('phone', e.target.value)} />
            </label>

            <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text pt-2">
              Bank & Steuer
            </p>
            <label className="block">
              <span className={labelClass}>Kontoinhaber</span>
              <input className={inputClass} value={localState.accountHolder} onChange={(e) => patch('accountHolder', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>Bank</span>
              <input className={inputClass} value={localState.bankName} onChange={(e) => patch('bankName', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>IBAN *</span>
              <input className={inputClass} value={localState.iban} onChange={(e) => patch('iban', e.target.value)} placeholder="DE…" />
            </label>
            <label className="block">
              <span className={labelClass}>BIC</span>
              <input className={inputClass} value={localState.bic} onChange={(e) => patch('bic', e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelClass}>Steuernummer</span>
                <input className={inputClass} value={localState.taxNumber} onChange={(e) => patch('taxNumber', e.target.value)} />
              </label>
              <label className="block">
                <span className={labelClass}>USt-IdNr.</span>
                <input className={inputClass} value={localState.vatId} onChange={(e) => patch('vatId', e.target.value)} />
              </label>
            </div>

            <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text pt-2">
              Empfänger (CUTIER / Michael Herzinger)
            </p>
            <label className="block">
              <span className={labelClass}>Firma</span>
              <input className={inputClass} value={localState.buyerCompanyName} onChange={(e) => patch('buyerCompanyName', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>Ansprechpartner</span>
              <input className={inputClass} value={localState.buyerContactPerson} onChange={(e) => patch('buyerContactPerson', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>E-Mail Empfänger</span>
              <input type="email" className={inputClass} value={localState.buyerEmail} onChange={(e) => patch('buyerEmail', e.target.value)} />
            </label>
            <label className="block">
              <span className={labelClass}>Straße Empfänger</span>
              <input className={inputClass} value={localState.buyerStreet} onChange={(e) => patch('buyerStreet', e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelClass}>PLZ</span>
                <input className={inputClass} value={localState.buyerZip} onChange={(e) => patch('buyerZip', e.target.value)} />
              </label>
              <label className="block">
                <span className={labelClass}>Ort</span>
                <input className={inputClass} value={localState.buyerCity} onChange={(e) => patch('buyerCity', e.target.value)} />
              </label>
            </div>
            <label className="block">
              <span className={labelClass}>USt-IdNr. Empfänger (optional)</span>
              <input className={inputClass} value={localState.buyerVatId} onChange={(e) => patch('buyerVatId', e.target.value)} />
            </label>

            {saveError ? <p className="text-xs text-red-400">{saveError}</p> : null}
          </div>
            <div className="flex gap-3 border-t border-pe-border-strong bg-pe-card px-6 py-4">
              <button
                type="button"
                onClick={handleDismiss}
                className="flex-1 rounded-xl border border-pe-border-strong py-3 text-xs font-bold uppercase tracking-widest text-pe-muted"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleSave()}
                className="flex-1 rounded-xl bg-[#E6C299] py-3 text-xs font-black uppercase tracking-widest text-black disabled:opacity-50"
              >
                {busy ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CutierInvoiceSettingsModal;
